import { FirebaseError } from "firebase/app";
import {
  collection,
  doc,
  DocumentData,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  QueryDocumentSnapshot,
  serverTimestamp,
  setDoc,
  Unsubscribe,
  where,
  writeBatch
} from "firebase/firestore";
import { RouteMarkerDraft, RouteMarkerType } from "../components/route-editor/routeMarkerTypes";
import { auth, db } from "../lib/firebase";

export type RouteEditorCapability = {
  label: string;
  status: "ready" | "placeholder";
};

export const routeEditorCapabilities: RouteEditorCapability[] = [
  { label: "Marker toolbox", status: "ready" },
  { label: "Click-on-map placement", status: "ready" },
  { label: "Marker inspector form", status: "ready" },
  { label: "Google Maps loader hook", status: "ready" },
  { label: "Firestore marker schema", status: "ready" },
  { label: "Event route context", status: "ready" },
  { label: "Drag/drop persistence", status: "ready" }
];

export type RouteMarkerStatus = "ACTIVE" | "DISABLED";

export type EventRouteMarker = RouteMarkerDraft & {
  eventId: string;
  status: RouteMarkerStatus;
  createdBy: string;
  createdAt: number | null;
  updatedAt: number | null;
};

export type RouteMarkersState = {
  markers: EventRouteMarker[];
  loading: boolean;
  error: string | null;
  permissionDenied: boolean;
};

type RouteMarkerSubscriptionOptions = {
  includeDisabled?: boolean;
};

const ROUTE_MARKER_TYPES = new Set<RouteMarkerType>([
  "START",
  "FINISH",
  "CONTROL",
  "WATER",
  "FOOD",
  "COFFEE",
  "WARNING",
  "SERVICE",
  "PHOTO",
  "NOTE"
]);

function routeMarkersCollection(eventId: string) {
  return collection(db, "events", eventId, "routeMarkers");
}

function routeMarkerDoc(eventId: string, markerId: string) {
  return doc(db, "events", eventId, "routeMarkers", markerId);
}

function stringValue(value: unknown, fallback = "") {
  return typeof value === "string" ? value : fallback;
}

function numberValue(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function booleanValue(value: unknown, fallback: boolean) {
  return typeof value === "boolean" ? value : fallback;
}

function kmInputValue(value: unknown): string {
  if (typeof value === "number" && Number.isFinite(value)) return value.toString();
  return stringValue(value);
}

function millisValue(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (value instanceof Date) return value.getTime();
  if (value && typeof value === "object") {
    const candidate = value as { toMillis?: () => number; seconds?: number };
    if (typeof candidate.toMillis === "function") return candidate.toMillis();
    if (typeof candidate.seconds === "number") return candidate.seconds * 1000;
  }
  return null;
}

function normalizeMarkerType(value: unknown): RouteMarkerType {
  const candidate = stringValue(value, "CONTROL") as RouteMarkerType;
  return ROUTE_MARKER_TYPES.has(candidate) ? candidate : "CONTROL";
}

function normalizeMarkerStatus(value: unknown): RouteMarkerStatus {
  return value === "DISABLED" ? "DISABLED" : "ACTIVE";
}

function trimTo(value: unknown, maxLength: number) {
  return stringValue(value).trim().slice(0, maxLength);
}

function kmWriteValue(value: string) {
  const normalized = value.trim().replace(",", ".");
  if (!normalized) return null;
  const numericValue = Number.parseFloat(normalized);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function permissionDeniedState(error: Error) {
  const code = error instanceof FirebaseError ? error.code : "";
  return code === "permission-denied" || error.message.toLowerCase().includes("permission");
}

function errorState(error: Error): RouteMarkersState {
  const permissionDenied = permissionDeniedState(error);
  return {
    markers: [],
    loading: false,
    error: permissionDenied ? "permission-denied" : error.message,
    permissionDenied
  };
}

function routeMarkerFromSnapshot(snapshot: QueryDocumentSnapshot<DocumentData>): EventRouteMarker | null {
  const data = snapshot.data();
  const lat = numberValue(data.lat);
  const lng = numberValue(data.lng);
  if (lat === null || lng === null) return null;

  return {
    id: stringValue(data.id, snapshot.id),
    eventId: stringValue(data.eventId),
    type: normalizeMarkerType(data.type),
    title: stringValue(data.title),
    description: stringValue(data.description),
    lat,
    lng,
    km: kmInputValue(data.km),
    openTime: stringValue(data.openTime),
    closeTime: stringValue(data.closeTime),
    visibleToAthletes: booleanValue(data.visibleToAthletes, true),
    order: numberValue(data.order) ?? 0,
    status: normalizeMarkerStatus(data.status),
    createdBy: stringValue(data.createdBy),
    createdAt: millisValue(data.createdAt),
    updatedAt: millisValue(data.updatedAt)
  };
}

function sortRouteMarkers<T extends RouteMarkerDraft>(markers: T[]) {
  return markers.slice().sort((a, b) => {
    const normalizedOrder = (marker: RouteMarkerDraft) =>
      Number.isFinite(marker.order) && marker.order > 0 ? marker.order : Number.MAX_SAFE_INTEGER;
    const normalizedKm = (marker: RouteMarkerDraft) => {
      const parsed = Number.parseFloat(marker.km.replace(",", "."));
      return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
    };
    const normalizedCreatedAt = (marker: RouteMarkerDraft) => {
      const candidate = marker as RouteMarkerDraft & { createdAt?: number | null };
      return candidate.createdAt ?? Number.MAX_SAFE_INTEGER;
    };

    const orderDiff = normalizedOrder(a) - normalizedOrder(b);
    if (orderDiff !== 0) return orderDiff;

    const kmDiff = normalizedKm(a) - normalizedKm(b);
    if (kmDiff !== 0) return kmDiff;

    const createdAtDiff = normalizedCreatedAt(a) - normalizedCreatedAt(b);
    return createdAtDiff !== 0 ? createdAtDiff : a.title.localeCompare(b.title, "el");
  });
}

function validateMarkerCoordinates(marker: RouteMarkerDraft) {
  if (!Number.isFinite(marker.lat) || marker.lat < -90 || marker.lat > 90) {
    throw new Error("invalid-marker-latitude");
  }
  if (!Number.isFinite(marker.lng) || marker.lng < -180 || marker.lng > 180) {
    throw new Error("invalid-marker-longitude");
  }
}

function markerWriteData(
  eventId: string,
  marker: RouteMarkerDraft,
  order: number,
  existingData: DocumentData | undefined,
  uid: string
) {
  validateMarkerCoordinates(marker);

  return {
    id: marker.id,
    eventId,
    type: ROUTE_MARKER_TYPES.has(marker.type) ? marker.type : "CONTROL",
    title: trimTo(marker.title, 120),
    description: trimTo(marker.description, 2000),
    lat: marker.lat,
    lng: marker.lng,
    km: kmWriteValue(marker.km),
    openTime: trimTo(marker.openTime, 16),
    closeTime: trimTo(marker.closeTime, 16),
    visibleToAthletes: Boolean(marker.visibleToAthletes),
    order,
    status: "ACTIVE" as RouteMarkerStatus,
    createdBy: stringValue(existingData?.createdBy, uid),
    createdAt: existingData?.createdAt ?? serverTimestamp(),
    updatedAt: serverTimestamp()
  };
}

export function isRouteMarkerPermissionDenied(error: unknown) {
  return error instanceof Error && permissionDeniedState(error);
}

export function subscribeEventRouteMarkers(
  eventId: string,
  callback: (state: RouteMarkersState) => void,
  options: RouteMarkerSubscriptionOptions = {}
): Unsubscribe {
  callback({ markers: [], loading: true, error: null, permissionDenied: false });

  return onSnapshot(
    routeMarkersCollection(eventId),
    (snapshot) => {
      const markers = snapshot.docs
        .map(routeMarkerFromSnapshot)
        .filter((marker): marker is EventRouteMarker => marker !== null)
        .filter((marker) => options.includeDisabled || marker.status === "ACTIVE");

      callback({
        markers: sortRouteMarkers(markers),
        loading: false,
        error: null,
        permissionDenied: false
      });
    },
    (error) => {
      callback(errorState(error));
    }
  );
}

export function subscribePublicEventRouteMarkers(
  eventId: string,
  callback: (state: RouteMarkersState) => void
): Unsubscribe {
  callback({ markers: [], loading: true, error: null, permissionDenied: false });

  const publicMarkersQuery = query(
    routeMarkersCollection(eventId),
    where("status", "==", "ACTIVE"),
    where("visibleToAthletes", "==", true)
  );

  return onSnapshot(
    publicMarkersQuery,
    (snapshot) => {
      const markers = snapshot.docs
        .map(routeMarkerFromSnapshot)
        .filter((marker): marker is EventRouteMarker => marker !== null);

      callback({
        markers: sortRouteMarkers(markers),
        loading: false,
        error: null,
        permissionDenied: false
      });
    },
    (error) => {
      callback(errorState(error));
    }
  );
}

export async function upsertEventRouteMarker(eventId: string, marker: RouteMarkerDraft) {
  const user = auth.currentUser;
  if (!user) throw new Error("auth-required");

  const markerRef = routeMarkerDoc(eventId, marker.id);
  const existingSnapshot = await getDoc(markerRef);
  await setDoc(markerRef, markerWriteData(eventId, marker, marker.order, existingSnapshot.data(), user.uid));
}

export async function disableEventRouteMarker(eventId: string, markerId: string) {
  const user = auth.currentUser;
  if (!user) throw new Error("auth-required");

  const markerRef = routeMarkerDoc(eventId, markerId);
  const existingSnapshot = await getDoc(markerRef);
  if (!existingSnapshot.exists()) return;

  await setDoc(markerRef, {
    ...existingSnapshot.data(),
    status: "DISABLED",
    updatedAt: serverTimestamp()
  });
}

export async function saveEventRouteMarkers(eventId: string, markers: RouteMarkerDraft[]) {
  const user = auth.currentUser;
  if (!user) throw new Error("auth-required");

  const existingSnapshot = await getDocs(routeMarkersCollection(eventId));
  const existingById = new Map(existingSnapshot.docs.map((snapshot) => [snapshot.id, snapshot.data()]));
  const activeMarkerIds = new Set(markers.map((marker) => marker.id));
  const batch = writeBatch(db);

  sortRouteMarkers(markers).forEach((marker, index) => {
    const markerRef = routeMarkerDoc(eventId, marker.id);
    batch.set(markerRef, markerWriteData(eventId, marker, index + 1, existingById.get(marker.id), user.uid));
  });

  existingSnapshot.docs.forEach((snapshot) => {
    const existing = snapshot.data();
    if (normalizeMarkerStatus(existing.status) === "ACTIVE" && !activeMarkerIds.has(snapshot.id)) {
      batch.set(routeMarkerDoc(eventId, snapshot.id), {
        ...existing,
        status: "DISABLED",
        updatedAt: serverTimestamp()
      });
    }
  });

  await batch.commit();

  return sortRouteMarkers(
    markers.map((marker, index) => ({
      ...marker,
      order: index + 1
    }))
  );
}
